import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import { Users, UserPlus, Loader, X } from 'lucide-react';
import teamsService from '../services/teamsService';
import ImageCropper from './ui/ImageCropper';

const TEAM_COLOR_PRESETS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6',
  '#ec4899', '#6366f1',
];

function randomPresetColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TEAM_COLOR_PRESETS[Math.abs(hash) % TEAM_COLOR_PRESETS.length];
}

function darken(hex, amt = 0.25) {
  if (!hex || hex.length < 7) return hex;
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  const f = v => Math.max(0, Math.round(v * (1 - amt)));
  const h = v => v.toString(16).padStart(2,'0');
  return `#${h(f(r))}${h(f(g))}${h(f(b))}`;
}

const TeamsModal = ({
  isOpen,
  onClose,
  mode = 'create',
  team = null,
  defaultInviteCode = null,
  onTeamCreated,
  onTeamUpdated,
  onTeamJoined,
  onModeSwitch,
  showToast,
  onUpgradeRequired,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({ name: '', description: '', is_public: false, color: '' });
  const [joinData, setJoinData]   = useState({ invite_code: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState('');
  const [logoFile, setLogoFile]   = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [cropSource, setCropSource] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && team) {
      setFormData({
        name:        team.name || '',
        description: team.description || '',
        is_public:   team.is_public || false,
        color:       team.color || randomPresetColor(team.name),
      });
      setLogoPreview(team.logo_url || null);
    } else if (mode === 'create') {
      setFormData({ name: '', description: '', is_public: false, color: TEAM_COLOR_PRESETS[0] });
      setLogoPreview(null);
    }
    setJoinData({ invite_code: defaultInviteCode || '' });
    setError('');
    setLogoFile(null);
    setCropSource(null);
  }, [team, mode, isOpen, defaultInviteCode]);

  // Update colour when name changes (create mode only)
  useEffect(() => {
    if (mode === 'create' && formData.name) {
      setFormData(prev => ({ ...prev, color: randomPresetColor(formData.name) }));
    }
  }, [formData.name, mode]);

  const handleInput = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleLogoChange = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCropSource(file); // open the cropper; the cropped result becomes the logo
  };

  const onLogoCropped = croppedFile => {
    setLogoFile(croppedFile);
    setLogoPreview(prev => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(croppedFile);
    });
    setCropSource(null);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      if (mode === 'create') {
        const newTeam = await teamsService.createTeam(formData);
        // upload logo if provided
        if (logoFile && newTeam?.id) {
          try { await teamsService.uploadTeamLogo(newTeam.id, logoFile); } catch {}
        }
        onTeamCreated?.(newTeam);
      } else if (mode === 'edit') {
        const updated = await teamsService.updateTeam(team.id, formData);
        if (logoFile) {
          try {
            const logoResult = await teamsService.uploadTeamLogo(team.id, logoFile);
            updated.logo_url = logoResult.logo_url;
          } catch {}
        }
        onTeamUpdated?.(updated);
      } else if (mode === 'join') {
        if (!joinData.invite_code.trim()) { setError(t('teams.inviteCodeRequired')); return; }
        const joined = await teamsService.joinTeam(joinData.invite_code);
        onTeamJoined?.(joined);
      }
      handleClose();
    } catch (err) {
      if (err.response?.data?.upgrade_required) {
        onUpgradeRequired?.(err.response.data.error || '');
        handleClose();
        return;
      }
      setError(err.response?.data?.error || err.message || t('teams.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', is_public: false, color: '' });
    setJoinData({ invite_code: '' });
    setError('');
    setLogoFile(null);
    setLogoPreview(null);
    setCropSource(null);
    onClose();
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return t('teams.modalCreate');
      case 'edit':   return t('teams.modalEdit');
      case 'join':   return t('teams.modalJoin');
      default:       return t('teams.modalDefault');
    }
  };

  const avatarLetter = (formData.name || '?').charAt(0).toUpperCase();
  const color = formData.color || TEAM_COLOR_PRESETS[0];

  const getSubtitle = () => {
    switch (mode) {
      case 'create': return 'Create a new team to collaborate with others';
      case 'edit':   return 'Update your team settings and members';
      case 'join':   return 'Enter an invite code to join an existing team';
      default:       return '';
    }
  };

  const getModalIcon = () => {
    switch (mode) {
      case 'join':   return <UserPlus size={24} className="text-blue-500" />;
      case 'create': return <Users size={24} className="text-blue-500" />;
      case 'edit':   return <Users size={24} className="text-blue-500" />;
      default:       return <Users size={24} className="text-blue-500" />;
    }
  };

  return (
    <>
    {isOpen && cropSource && (
      <ImageCropper
        file={cropSource}
        title="Crop team logo"
        saveLabel="Use image"
        onCancel={() => setCropSource(null)}
        onCropped={onLogoCropped}
      />
    )}
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()} subtitle={getSubtitle()} customIcon={getModalIcon()}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/40">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {mode === 'join' ? (
          <>
            <div className="mb-4 text-center">
              <div className="sidebar-empty-icon-wrap">
                <div className="sidebar-empty-icon">
                  <UserPlus size={16} />
                </div>
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Enter the team invitation code to join an existing team
              </p>
            </div>
            <input
              type="text"
              name="invite_code"
              value={joinData.invite_code}
              onChange={e => setJoinData({ invite_code: e.target.value })}
              placeholder={t('teams.inviteCodePlaceholder')}
              className="modal-input"
              // 0.05em, not 0.15em: at 26 characters the wider tracking pushed
              // the code past the field on a narrow modal. Still spaced enough
              // to read in groups, just not spaced for an 8-character code.
              style={{ textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              // Matches Team.invite_code_length (26) — codes are 16 random bytes
              // base32-encoded without padding. This said 8, from when the code
              // was varchar(8)/32 bits; migration 20260803000003 widened it to
              // 128 bits and this input was not moved with it, so a real code
              // was silently truncated as you typed and the join always failed.
              maxLength={26}
              required
            />
          </>
        ) : (
          <>
            {/* Logo upload zone */}
            <div className="team-logo-zone" onClick={() => fileInputRef.current?.click()}>
              <div style={{
                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                background: logoPreview ? 'transparent' : `linear-gradient(135deg, ${color} 0%, ${darken(color, 0.3)} 100%)`,
                fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, color: '#fff', userSelect: 'none', overflow: 'hidden',
              }}>
                {logoPreview
                  ? <img src={logoPreview} alt="logo" style={{ width: 44, height: 44, objectFit: 'cover', display: 'block' }} />
                  : avatarLetter
                }
              </div>
              <span className="team-logo-zone-label" style={{ flex: 1 }}>Upload Logo</span>
              <span className="modal-logo-hint">PNG, JPG or GIF · max 2 MB</span>
              {logoPreview && (
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); removeLogo(); }}
                  style={{ marginLeft: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', padding: 0, flexShrink: 0 }}
                >
                  <X size={14} />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleLogoChange}
              />
            </div>

            {/* Name */}
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInput}
              placeholder="Team name"
              className="modal-input"
              required
            />

            {/* Colour picker */}
            {!logoPreview && (
              <div>
                <p className="mb-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">Team colour</p>
                <div className="flex flex-wrap gap-2">
                  {TEAM_COLOR_PRESETS.map(hex => (
                    <button
                      key={hex}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: hex }))}
                      style={{ background: hex, width: 28, height: 28, borderRadius: 7 }}
                      className={`flex-shrink-0 transition-transform hover:scale-110 focus:outline-none ${
                        formData.color === hex ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110' : ''
                      }`}
                      aria-label={hex}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInput}
              placeholder={t('teams.teamDescPlaceholder')}
              rows={3}
              className="modal-form-textarea"
            />

            {/* Public toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_public"
                name="is_public"
                checked={formData.is_public}
                onChange={handleInput}
                className="checkbox-brand cursor-pointer"
              />
              <label htmlFor="is_public" className="text-sm text-gray-700 dark:text-zinc-300">
                {t('teams.makePublic')}
              </label>
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-3 px-4 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{ background: 'var(--accent)', color: 'var(--accent-fg, #fff)' }}
        >
          {isLoading && <Loader className="animate-spin" size={15} />}
          <span>{isLoading ? t('common.pleaseWait') : getTitle()}</span>
        </button>

        {mode !== 'edit' && (
          <div className="border-t border-gray-200 pt-4 text-center dark:border-zinc-800">
            {mode === 'create' ? (
              <p className="text-sm text-gray-600 dark:text-zinc-500">
                {t('teams.hasInvitation')}{' '}
                <button type="button"
                  onClick={() => { onClose(); setTimeout(() => onModeSwitch?.('join'), 100); }}
                  className="bg-transparent font-medium transition-colors hover:opacity-80"
                  style={{ color: 'var(--accent)' }}>
                  {t('teams.joinATeam')}
                </button>
              </p>
            ) : (
              <p className="text-sm text-gray-600 dark:text-zinc-500">
                {t('teams.wantCreate')}{' '}
                <button type="button"
                  onClick={() => { onClose(); setTimeout(() => onModeSwitch?.('create'), 100); }}
                  className="bg-transparent font-medium transition-colors hover:opacity-80"
                  style={{ color: 'var(--accent)' }}>
                  {t('teams.createATeam')}
                </button>
              </p>
            )}
          </div>
        )}
      </form>
    </Modal>
    </>
  );
};

export default TeamsModal;
